import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../environments/environment';
import { UserSessionStateService } from './user-session-state.service';

export interface RequestOptions {
    headers?: Record<string, string | string[]>;
    params?: Record<string, string | number | boolean | null | undefined>;
    withCredentials?: boolean;
}

// Matches exactly what HttpClient json-overloads accept
interface HttpJsonOptions {
    headers?: Record<string, string | string[]>;
    withCredentials: boolean;
    responseType: 'json';
}

/**
 * Single entry point for all HTTP requests.
 *
 * Responsibilities:
 *  - Prepends `environment.apiUrl` to every request
 *  - Serialises `params` into `HttpParams` (skips null/undefined values)
 *  - Provides typed `get / post / put / patch / delete` helpers
 *
 * Cross-cutting concerns (auth headers, trace-id) are handled by ApiInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class FetcherService {
    private readonly http = inject(HttpClient);
    private readonly userSession = inject(UserSessionStateService);
    private readonly baseUrl = environment.apiUrl;

    get<R>(url: string, params?: RequestOptions['params'], options: RequestOptions = {}): Observable<R> {
        return this.http.get<R>(`${this.baseUrl}${url}`, {
            ...this.buildOptions(options),
            params: this.buildParams(params),
        });
    }

    post<R>(url: string, body: unknown, options: RequestOptions = {}): Observable<R> {
        return this.http.post<R>(`${this.baseUrl}${url}`, body, this.buildOptions(options));
    }

    put<R>(url: string, body: unknown, options: RequestOptions = {}): Observable<R> {
        return this.http.put<R>(`${this.baseUrl}${url}`, body, this.buildOptions(options));
    }

    patch<R>(url: string, body: unknown, options: RequestOptions = {}): Observable<R> {
        return this.http.patch<R>(`${this.baseUrl}${url}`, body, this.buildOptions(options));
    }

    delete<R>(url: string, options: RequestOptions = {}): Observable<R> {
        return this.http.delete<R>(`${this.baseUrl}${url}`, this.buildOptions(options));
    }

    postStream(url: string, body: unknown, options: RequestOptions = {}): Observable<string> {
        const fullUrl = `${this.baseUrl}${url}`;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-Request-Id': `client-${uuidv4()}`,
            ...((options.headers as Record<string, string>) || {}),
        };
        const token = this.userSession.getSessionId();
        if (token) {
            headers['X-PLG-Session'] = token;
        }

        return new Observable(observer => {
            fetch(fullUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
            })
                .then(response => {
                    if (!response.ok) {
                        return response.text().then(body => {
                            let parsed: any;
                            try {
                                parsed = JSON.parse(body);
                            } catch {
                                parsed = {};
                            }
                            const code = parsed?.extensions?.code;

                            // Native fetch() bypasses Angular's HTTP_INTERCEPTORS, so we
                            // mirror the two latching interceptors here for the SSE path.
                            // Without this, an expired-session 401 surfaces as an inline
                            // red error in the chat instead of the full-screen landing.
                            if (response.status === 401 && code === 'SESSION_EXPIRED') {
                                this.userSession.notifySessionExpired();
                                observer.complete();
                                return;
                            }
                            if (response.status === 429 && code === 'RATE_LIMIT_EXCEEDED') {
                                const msg = typeof parsed?.message === 'string' ? parsed.message : null;
                                this.userSession.notifyRateLimited(msg);
                            }

                            const detail = parsed.detail;
                            const message =
                                parsed.message ||
                                (Array.isArray(detail) ? detail.map((d: any) => d.msg).join('; ') : detail) ||
                                `HTTP ${response.status}`;
                            const err: any = new Error(message);
                            err.status = response.status;
                            err.error = parsed;
                            observer.error(err);
                        });
                    }

                    const reader = response.body?.getReader();
                    const decoder = new TextDecoder();

                    const readStream = () => {
                        reader
                            ?.read()
                            .then(({ done, value }) => {
                                if (done) {
                                    observer.complete();
                                    return;
                                }
                                const chunk = decoder.decode(value, { stream: true });
                                observer.next(chunk);
                                readStream();
                            })
                            .catch(error => observer.error(error));
                    };

                    readStream();
                })
                .catch(error => observer.error(error));

            return () => {};
        });
    }

    // ── Private helpers ────────────────────────────────────────────────────────

    private buildParams(params?: RequestOptions['params']): HttpParams {
        let httpParams = new HttpParams();

        if (!params) return httpParams;

        for (const [key, value] of Object.entries(params)) {
            if (value != null) {
                httpParams = httpParams.set(key, String(value));
            }
        }

        return httpParams;
    }

    private buildOptions(options: RequestOptions): HttpJsonOptions {
        return {
            headers: options.headers,
            responseType: 'json',
            withCredentials: options.withCredentials ?? false,
        };
    }
}
